/**
 * SideEditor — Node editor panel
 * Per PLAN.md ui/SideEditor.js and PRD §5.4
 */

import { eventBus } from '../core/EventBus.js';
import { renderLatex } from '../latex/Renderer.js';
import { debounce } from '../utils/debounce.js';
import { LATEX_DEBOUNCE } from '../utils/constants.js';

export class SideEditor {
    /**
     * @param {HTMLElement} container 
     */
    constructor(container) {
        this.container = container;
        this.element = null;
        this.isVisible = false;
        this.currentNodeId = null;
        this.currentType = 'latex';
        this.onContentChange = null;
        this.onTypeChange = null;
        this.onConfirm = null;
        this.onCancel = null;

        // Debounced preview update
        this.updatePreviewDebounced = debounce(this.updatePreview.bind(this), LATEX_DEBOUNCE);

        this.init();
    }

    init() {
        this.element = document.createElement('div');
        this.element.className = 'side-editor';
        this.element.style.display = 'none';
        this.element.innerHTML = `
      <textarea class="side-editor-input" rows="3" placeholder="Enter equation..."></textarea>
      <div class="side-editor-toggle">
        <button class="active" data-type="latex">LaTeX</button>
        <button data-type="text">Text</button>
      </div>
      <div class="side-editor-preview-label" style="font-size: 11px; color: var(--text-secondary); margin-bottom: 4px;">Preview:</div>
      <div class="side-editor-preview"></div>
    `;

        this.container.appendChild(this.element);

        this.input = this.element.querySelector('.side-editor-input');
        this.preview = this.element.querySelector('.side-editor-preview');
        this.toggleBtns = this.element.querySelectorAll('.side-editor-toggle button');

        this.bindEvents();
        this.bindGlobalEvents();
    }

    bindEvents() {
        // Input changes
        this.input.addEventListener('input', () => {
            if (this.onContentChange) {
                this.onContentChange(this.input.value);
            }
            this.updatePreviewDebounced();
        });

        // Type toggle
        this.toggleBtns.forEach(btn => {
            btn.addEventListener('click', () => {
                this.setType(btn.dataset.type);
                if (this.onTypeChange) {
                    this.onTypeChange(btn.dataset.type);
                }
            });
        });

        // Key handlers
        this.input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (this.onConfirm) {
                    this.onConfirm();
                }
            } else if (e.key === 'Escape') {
                if (this.onCancel) {
                    this.onCancel();
                }
            } else if ((e.metaKey || e.ctrlKey) && e.key === 't') {
                // Toggle between LaTeX and Text mode with Cmd+T
                e.preventDefault();
                const newType = this.currentType === 'latex' ? 'text' : 'latex';
                this.setType(newType);
                if (this.onTypeChange) {
                    this.onTypeChange(newType);
                }
            }
        });

        // Prevent clicks from bubbling to canvas
        this.element.addEventListener('mousedown', (e) => {
            e.stopPropagation();
        });
    }

    bindGlobalEvents() {
        // Listen for show requests
        eventBus.on('editor:show', ({ nodeId, content, type, position }) => {
            this.show(nodeId, content, type, position);
        });

        eventBus.on('editor:hide', () => {
            this.hide();
        });
    }

    /**
     * Show editor for a node
     * @param {string} nodeId 
     * @param {string} content 
     * @param {string} type 
     * @param {{x: number, y: number}} position 
     */
    show(nodeId, content, type, position) {
        this.currentNodeId = nodeId;
        this.currentType = type;

        // Set content
        this.input.value = content;
        this.setType(type);
        this.updatePreview();

        // Position the editor
        this.element.style.left = `${position.x}px`;
        this.element.style.top = `${position.y}px`;

        // Ensure editor stays in viewport
        const rect = this.element.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        if (position.x + rect.width > viewportWidth - 20) {
            this.element.style.left = `${viewportWidth - rect.width - 20}px`;
        }
        if (position.y + rect.height > viewportHeight - 20) {
            this.element.style.top = `${viewportHeight - rect.height - 20}px`;
        }

        // Show and focus
        this.element.style.display = 'block';
        this.isVisible = true;

        // Focus input and select content
        requestAnimationFrame(() => {
            this.input.focus();
            this.input.select();
        });
    }

    /**
     * Hide editor
     */
    hide() {
        this.element.style.display = 'none';
        this.isVisible = false;
        this.currentNodeId = null;
    }

    /**
     * Set the node type
     * @param {'latex'|'text'} type 
     */
    setType(type) {
        this.currentType = type;
        this.toggleBtns.forEach(btn => {
            btn.classList.toggle('active', btn.dataset.type === type);
        });
        this.input.placeholder = type === 'latex' ? 'Enter equation...' : 'Enter text...';
        this.updatePreview();
    }

    /**
     * Update preview
     */
    updatePreview() {
        const content = this.input.value;

        if (this.currentType === 'latex') {
            this.preview.innerHTML = renderLatex(content);
        } else {
            this.preview.textContent = content || 'Empty';
        }
    }

    /**
     * Get current content
     * @returns {string}
     */
    getContent() {
        return this.input.value;
    }

    /**
     * Check if visible
     * @returns {boolean}
     */
    getIsVisible() {
        return this.isVisible;
    }

    /**
     * Destroy editor
     */
    destroy() {
        this.element.remove();
    }
}
